import { useCallback, useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { CaretDown } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { useSystemAbout } from "@/lib/useSystemAbout";
import { useMorningOperationsProgress } from "@/lib/useMorningOperationsProgress";
import {
  buildNavigation,
  buildQuickActions,
  WORKSPACE_REGISTRY,
} from "@/nav/workspaceRegistry";

// DSG-001 compliant — colours resolved via semantic Tailwind tokens
// where possible. Workspace accents intentionally use the built-in
// Tailwind palette (matches existing footer conventions) to keep
// each workspace visually distinct without inventing new tokens.
//
// Sprint 6 · LAW-007 · Presentation must not define architecture.
// This file is a pure renderer: no nav arrays, no permission mapping,
// no ordering — those all live in `workspaceRegistry.js`.

const ACCENT_TEXT = {
  amber:  "text-amber-600",
  red:    "text-red-600",
  blue:   "text-blue-600",
  purple: "text-purple-600",
  slate:  "text-slate-500",
};

const ACCENT_ACTIVE_BAR = {
  amber:  "bg-amber-500",
  red:    "bg-red-500",
  blue:   "bg-blue-500",
  purple: "bg-purple-500",
  slate:  "bg-slate-400",
};

const STORAGE_KEY = "coolman.sidebar.workspaces.v1";

function loadPersistedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function persistState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / privacy-mode failures */
  }
}

function initialExpandedState() {
  const persisted = loadPersistedState();
  return WORKSPACE_REGISTRY.reduce((acc, ws) => {
    acc[ws.id] =
      persisted && typeof persisted[ws.id] === "boolean"
        ? persisted[ws.id]
        : !!ws.defaultExpanded;
    return acc;
  }, {});
}

export default function Sidebar({ onNavigate }) {
  const { user } = useAuth();
  const { branding, primaryLogoSrc } = useBranding();
  const about = useSystemAbout();
  const role = user?.role;
  const workspaces = buildNavigation(role);
  const quickActions = buildQuickActions(role);
  const displayName = branding?.identity?.display_name || "Coolman Fuels";
  const productName = about?.product || "Coolman Operations";
  const versionLabel = about
    ? `Version ${about.version}${about.channel ? ` ${about.channel}` : ""}`
    : "Version …";
  const environment = about?.environment || "";
  const envTone =
    environment === "Production"
      ? "text-emerald-600"
      : environment === "Preview"
        ? "text-amber-600"
        : environment === "Development"
          ? "text-slate-500"
          : "text-ink-muted";
  const morningProgress = useMorningOperationsProgress();
  const morningIncomplete =
    morningProgress.overallStatus && morningProgress.overallStatus !== "COMPLETE";

  const [expanded, setExpanded] = useState(initialExpandedState);

  useEffect(() => {
    persistState(expanded);
  }, [expanded]);

  const toggle = useCallback((wsId) => {
    setExpanded((prev) => ({ ...prev, [wsId]: !prev[wsId] }));
  }, []);

  return (
    <aside
      data-testid="sidebar"
      className="flex h-full w-60 flex-col border-r border-divider bg-surface-alt"
    >
      <div className="flex h-20 items-center justify-center border-b border-divider bg-surface px-4">
        <Link
          to="/"
          data-testid="sidebar-logo-link"
          className="flex h-full items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-md"
          aria-label="Go to dashboard"
          title="Go to dashboard"
        >
          <img
            src={primaryLogoSrc()}
            alt={displayName}
            className="h-14 w-auto object-contain"
            data-testid="sidebar-logo"
          />
        </Link>
      </div>

      <nav
        className="flex-1 overflow-y-auto px-3 py-4"
        data-testid="sidebar-nav"
      >
        {/* Quick Actions — actions, not destinations. Visually distinct. */}
        {quickActions.length > 0 && (
          <div className="mb-4" data-testid="quick-actions">
            <div className="mb-1 px-3 text-[11px] font-bold uppercase tracking-wider text-ink-muted">
              Quick Actions
            </div>
            <ul className="space-y-1">
              {quickActions.map(({ to, label, icon: Icon, testid }) => (
                <li key={to + label}>
                  <NavLink
                    to={to}
                    data-testid={testid}
                    onClick={onNavigate}
                    className="flex items-center gap-3 rounded-md border border-brand/40 bg-brand/5 px-3 py-2 text-[14px] font-semibold text-brand hover:bg-brand hover:text-brand-foreground"
                  >
                    {Icon ? <Icon size={18} weight="bold" /> : null}
                    <span className="flex-1">{label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div
          className="mx-3 mb-3 border-t border-divider"
          data-testid="quick-actions-divider"
          aria-hidden="true"
        />

        {/* Workspaces — collapsible groups derived from the Workspace Registry. */}
        {workspaces.map((ws) => {
          const WsIcon = ws.icon;
          const isOpen = !!expanded[ws.id];
          const accentText = ACCENT_TEXT[ws.accent] || "text-ink-muted";
          const accentBar = ACCENT_ACTIVE_BAR[ws.accent] || "bg-brand";
          const hasProgressBadge = ws.visibleComponents.some(
            (c) => c.progressBadge
          );

          return (
            <div key={ws.id} className="mb-3" data-testid={ws.testid}>
              <button
                type="button"
                onClick={() => toggle(ws.id)}
                aria-expanded={isOpen}
                aria-controls={`${ws.id}-panel`}
                data-testid={`${ws.testid}-toggle`}
                className="group flex w-full items-center gap-2 rounded-md px-3 py-2 text-left transition-colors hover:bg-surface"
              >
                <span
                  className={`inline-flex h-6 rounded-full ${accentBar} ${
                    ws.primary ? "w-[3px]" : "w-1"
                  }`}
                  aria-hidden="true"
                />
                {WsIcon ? (
                  <WsIcon
                    size={ws.primary ? 18 : 16}
                    weight={ws.primary ? "fill" : "regular"}
                    className={accentText}
                  />
                ) : null}
                <span
                  className={`flex-1 uppercase tracking-wider ${
                    ws.primary
                      ? "text-[12px] font-extrabold text-charcoal"
                      : "text-[11px] font-bold text-ink-secondary"
                  }`}
                >
                  {ws.label}
                </span>
                {!isOpen && hasProgressBadge && morningIncomplete && (
                  <span
                    data-testid={`${ws.testid}-collapsed-badge`}
                    className="inline-flex items-center rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white"
                    title={`Start the Day · ${morningProgress.completed}/${morningProgress.total} steps`}
                  >
                    {morningProgress.completed}/{morningProgress.total}
                  </span>
                )}
                <CaretDown
                  size={12}
                  weight="bold"
                  className={`text-ink-muted transition-transform duration-200 ${
                    isOpen ? "rotate-0" : "-rotate-90"
                  }`}
                />
              </button>

              <div
                id={`${ws.id}-panel`}
                data-testid={`${ws.testid}-panel`}
                aria-hidden={!isOpen}
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ease-out ${
                  isOpen
                    ? "max-h-[500px] opacity-100"
                    : "pointer-events-none max-h-0 opacity-0"
                }`}
              >
                <ul className="space-y-0.5 pt-1">
                  {ws.visibleComponents.map(
                    ({ to, label, icon: Icon, testid, progressBadge }) => (
                      <li key={to + label}>
                        <NavLink
                          to={to}
                          data-testid={testid}
                          onClick={onNavigate}
                          className={({ isActive }) =>
                            [
                              "flex items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium",
                              isActive
                                ? "bg-brand text-brand-foreground"
                                : "text-charcoal hover:bg-surface",
                            ].join(" ")
                          }
                        >
                          {Icon ? <Icon size={18} weight="regular" /> : null}
                          <span className="flex-1">{label}</span>
                          {progressBadge && morningIncomplete && (
                            <span
                              data-testid="nav-start-the-day-progress"
                              className="inline-flex items-center rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-bold leading-none text-white"
                              title={`${morningProgress.completed} of ${morningProgress.total} steps complete`}
                            >
                              {morningProgress.completed}/{morningProgress.total}
                            </span>
                          )}
                        </NavLink>
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          );
        })}
      </nav>

      <div
        className="border-t border-divider px-4 py-3 text-xs"
        data-testid="sidebar-footer"
      >
        <div
          data-testid="sidebar-product-name"
          className="font-semibold text-ink-secondary"
        >
          {productName}
        </div>
        <div
          data-testid="sidebar-version"
          className="mt-0.5 font-mono text-[11px] text-ink-muted"
        >
          {versionLabel}
        </div>
        {environment && (
          <div
            data-testid="sidebar-environment"
            className={`mt-0.5 text-[11px] font-semibold uppercase tracking-wider ${envTone}`}
          >
            {environment}
          </div>
        )}
        <div className="mt-2 text-[11px] text-ink-muted">Ontario, Canada</div>
      </div>
    </aside>
  );
}
